import React from 'react';
import PropTypes from 'prop-types';

import {
  SR_HITLIST_STATUS_STARTED,
  SR_HITLIST_TASK_TYPE_FAILED_TOUR,
  SR_HITLIST_TASK_CALL_OUTCOME_ANSWERED,
  SR_HITLIST_TASK_CALL_OUTCOME_NO_CALL_NEEDED,
  SR_HITLIST_TASK_TYPE_PREQUAL_PENDING_APP,
  SR_HITLIST_TASK_TYPE_PENDING_APP_COMPLETED,
  SR_HITLIST_TASK_TYPE_PENDING_SECURITY_DEPOSIT,
} from 'common/hitlistTaskConstants';

import { RENTLINX_LISTING_LEAD_STATUS_ACTIVE } from 'common/rentlinxConstants';

import SrBox from 'client/components/SrBox';
import ButtonDeprecated, { BUTTON_THEME_BLACK_GREYCLIFF } from 'client/components/ButtonDeprecated';
import SrRentlinxLeadNotes from 'client/components/SrRentlinxLeadNotes';
import SrAdminHouseholdNotes from 'client/components/SrAdminHouseholdNotes/SrAdminHouseholdNotes';
import HitlistTaskListRowHeader from 'client/components/HitlistTaskList/HitlistTaskListRowHeader';
import HitlistTaskListRowBackupCodes from 'client/components/HitlistTaskList/HitlistTaskListRowBackupCodes';
import HitlistTaskListRowHousehold from 'client/components/HitlistTaskList/HitlistTaskListRowHousehold';
import HitlistTaskListRowCallScript from 'client/components/HitlistTaskList/HitlistTaskListRowCallScript';
import HitlistTaskListRowCallOutcome from 'client/components/HitlistTaskList/HitlistTaskListRowCallOutcome';
import HitlistTaskListRowTourFeedback from 'client/components/HitlistTaskList/HitlistTaskListRowTourFeedback';
import HitlistTaskListRowArchiveLead from 'client/components/HitlistTaskList/HitlistTaskListRowArchiveLead';
import HitlistTaskSendFollowUpTextButton from 'client/components/HitlistTaskList/HitlistTaskSendFollowUpTextButton';
import SrReportBugButton from 'client/components/SrReportBugButton';
import SrFrontConversation from 'client/components/SrFrontConversation';
import SrText from 'client/components/SrText';

import './HitlistTaskListRow.less';

const HitlistTaskListRow = ({
  hitlistTask,
  renter,
  partner,
  listing,
  tourListing,
  review,
  household,
  rentlinxLead,
  rentlinxListingLead,
  currentUser,
  assignedUser,
  onButtonClick,
  onUpdate,
  dispatch,
  getHitlistTasks,
  hitlistCallScripts,
  concession,
  frontConversation,
  householdTasksCount,
}) => {
  const isAssignedUser = assignedUser?.id === currentUser.id;
  const showDoneButton =
    isAssignedUser &&
    householdTasksCount > 1 &&
    [SR_HITLIST_TASK_CALL_OUTCOME_ANSWERED, SR_HITLIST_TASK_CALL_OUTCOME_NO_CALL_NEEDED].includes(
      hitlistTask.callOutcome,
    ) &&
    [
      SR_HITLIST_TASK_TYPE_PREQUAL_PENDING_APP,
      SR_HITLIST_TASK_TYPE_PENDING_APP_COMPLETED,
      SR_HITLIST_TASK_TYPE_PENDING_SECURITY_DEPOSIT,
    ].includes(hitlistTask.type);

  return (
    <div styleName="row">
      <HitlistTaskListRowHeader
        hitlistTask={hitlistTask}
        renter={renter || rentlinxLead}
        listing={listing}
        partner={partner}
        currentUser={currentUser}
        assignedUser={assignedUser}
        isAssignedUser={isAssignedUser}
        onButtonClick={onButtonClick}
        householdTasksCount={householdTasksCount}
      />
      {showDoneButton && (
        <SrBox padding="4px">
          <ButtonDeprecated
            theme={BUTTON_THEME_BLACK_GREYCLIFF}
            className="hitlistDone"
            onClick={() => onButtonClick({ hitlistTask, cancelSameHouseholdTasks: true })}
          >
            Done and no other renter in this household needs a call
          </ButtonDeprecated>
        </SrBox>
      )}
      {hitlistTask.status === SR_HITLIST_STATUS_STARTED && isAssignedUser && (
        <div styleName="body">
          {frontConversation && (
            <SrFrontConversation
              ctaClassName="blue-link"
              frontConversation={frontConversation}
              user={renter}
              dispatch={dispatch}
            />
          )}
          {!frontConversation && <SrText font="greycliff">No Front inbox found...</SrText>}
          <HitlistTaskListRowCallScript
            hitlistTask={hitlistTask}
            hitlistCallScripts={hitlistCallScripts}
            concession={concession}
            listing={listing}
          />
          <HitlistTaskListRowCallOutcome hitlistTask={hitlistTask} onUpdate={onUpdate} />
          {review && (
            <HitlistTaskListRowTourFeedback
              review={review}
              dispatch={dispatch}
              getHitlistTasks={getHitlistTasks}
            />
          )}
          <HitlistTaskSendFollowUpTextButton
            hitlistTask={hitlistTask}
            getHitlistTasks={getHitlistTasks}
          />
          {hitlistTask.type === SR_HITLIST_TASK_TYPE_FAILED_TOUR && (
            <HitlistTaskListRowBackupCodes
              listing={listing}
              renter={renter}
              tourListing={tourListing}
              getHitlistTasks={getHitlistTasks}
            />
          )}
          {renter && <HitlistTaskListRowHousehold renter={renter} />}
          {household && (
            <SrAdminHouseholdNotes
              householdId={household.id}
              listingsApplyingFor={[listing]}
              dispatch={dispatch}
              currentUser={currentUser}
              height="calc(40vh - 16px)"
            />
          )}
          {rentlinxLead && (
            <SrRentlinxLeadNotes
              height="calc(40vh - 16px)"
              listingIds={[listing.id]}
              rentlinxLeadId={rentlinxLead.id}
              currentUser={currentUser}
              dispatch={dispatch}
            />
          )}
          {rentlinxListingLead &&
            rentlinxListingLead.status === RENTLINX_LISTING_LEAD_STATUS_ACTIVE && (
              <HitlistTaskListRowArchiveLead
                rentlinxListingLeadId={rentlinxListingLead.id}
                dispatch={dispatch}
                getHitlistTasks={getHitlistTasks}
              />
            )}
          <SrReportBugButton
            userId={renter?.id}
            listingId={listing.id}
            section="hitlistDashboard"
          />
        </div>
      )}
    </div>
  );
};

HitlistTaskListRow.displayName = 'HitlistTaskListRow';

HitlistTaskListRow.propTypes = {
  hitlistTask: PropTypes.object.isRequired,
  renter: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  tourListing: PropTypes.object,
  review: PropTypes.object,
  household: PropTypes.object,
  rentlinxLead: PropTypes.object,
  rentlinxListingLead: PropTypes.object,
  currentUser: PropTypes.object.isRequired,
  assignedUser: PropTypes.object,
  onButtonClick: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  getHitlistTasks: PropTypes.func.isRequired,
  hitlistCallScripts: PropTypes.object.isRequired,
  concession: PropTypes.object,
  frontConversation: PropTypes.object,
};

export default HitlistTaskListRow;
